import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useAuthStore } from '@/stores/authStore';

/**
 * Step ids as defined by Momentum.Domain (AccountSetup). They are a contract shared with the API —
 * the organization endpoint returns completion keyed by these, so they must not drift.
 */
export const LAUNCH_STEP_IDS = {
    products: 'setup-products',
    customers: 'setup-customers',
    priceTiers: 'setup-price-tiers',
    paymentTerms: 'setup-payment-terms',
    orderTypes: 'setup-order-types',
    fulfillmentMethods: 'setup-fulfillment-methods',
    orderStatuses: 'setup-order-statuses'
} as const;

export interface LaunchChecklistItem {
    stepId: string;
    label: string;
    icon: string;
    /** Where the item sends you. Settings items deep-link to their section. */
    to: string;
    /** Which group the item is listed under in the modal. */
    group: 'nav' | 'settings';
    /** For settings items, the Settings.vue section key this item configures. */
    sectionKey?: string;
    isComplete: boolean;
}

type ItemDefinition = Omit<LaunchChecklistItem, 'isComplete'>;

/**
 * The seven things an organization configures before it can trade, in the order the design lists
 * them. Icons match the ones the sidebar and settings navigation already use for these pages.
 */
const ITEM_DEFINITIONS: ItemDefinition[] = [
    { stepId: LAUNCH_STEP_IDS.products, label: 'Products', icon: 'pi pi-shopping-bag', to: '/products', group: 'nav' },
    { stepId: LAUNCH_STEP_IDS.customers, label: 'Customers', icon: 'pi pi-users', to: '/customers', group: 'nav' },
    { stepId: LAUNCH_STEP_IDS.priceTiers, label: 'Price Tiers', icon: 'pi pi-dollar', to: '/settings?section=priceTiers', group: 'settings', sectionKey: 'priceTiers' },
    { stepId: LAUNCH_STEP_IDS.paymentTerms, label: 'Payment Terms', icon: 'pi pi-calendar', to: '/settings?section=paymentTerms', group: 'settings', sectionKey: 'paymentTerms' },
    { stepId: LAUNCH_STEP_IDS.orderTypes, label: 'Order Types', icon: 'pi pi-shopping-cart', to: '/settings?section=orderTypes', group: 'settings', sectionKey: 'orderTypes' },
    { stepId: LAUNCH_STEP_IDS.fulfillmentMethods, label: 'Fulfillment Methods', icon: 'pi pi-truck', to: '/settings?section=fulfillmentMethods', group: 'settings', sectionKey: 'fulfillmentMethods' },
    { stepId: LAUNCH_STEP_IDS.orderStatuses, label: 'Order Statuses', icon: 'pi pi-flag', to: '/settings?section=orderStatuses', group: 'settings', sectionKey: 'orderStatuses' }
];

const SEEN_STORAGE_PREFIX = 'nymble.launchChecklist.seen.';

/** The nav entry that stands in for every settings item. */
const SETTINGS_ROUTE = '/settings';

export const useLaunchChecklistStore = defineStore('launchChecklist', () => {
    const authStore = useAuthStore();
    const modalVisible = ref(false);

    const steps = computed(() => authStore.organization?.accountSetup?.steps ?? []);

    /**
     * Whether the organization actually came back with the go-live steps. An API that predates them
     * returns the old step set, and reading that as "nothing is set up" would put a Required tag on
     * every page — so every surface stays hidden until at least one of the seven is present.
     */
    const hasChecklist = computed(() =>
        steps.value.some((step) => ITEM_DEFINITIONS.some((item) => item.stepId === step.stepId))
    );

    const items = computed<LaunchChecklistItem[]>(() =>
        ITEM_DEFINITIONS.map((definition) => ({
            ...definition,
            isComplete: steps.value.find((step) => step.stepId === definition.stepId)?.isComplete === true
        }))
    );

    const isComplete = computed(() => hasChecklist.value && items.value.every((item) => item.isComplete));

    const remainingCount = computed(() => items.value.filter((item) => !item.isComplete).length);

    /** Once an organization is set up the checklist is done with — nothing about it is shown again. */
    const isActive = computed(() => hasChecklist.value && !isComplete.value);

    const navItems = computed(() => items.value.filter((item) => item.group === 'nav'));
    const settingsItems = computed(() => items.value.filter((item) => item.group === 'settings'));

    /**
     * Whether the Settings nav entry still needs attention. Five of the seven items live behind it,
     * so it carries a tag until the last of them is done — otherwise the only hint that something
     * is outstanding is a tag you have to open Settings to see.
     */
    const isSettingsRequired = computed(
        () => isActive.value && settingsItems.value.some((item) => !item.isComplete)
    );

    /** Whether a route still needs attention — drives the Required tag in the sidebar. */
    const isRouteRequired = (path: string) => {
        if (path === SETTINGS_ROUTE) return isSettingsRequired.value;
        return isActive.value && items.value.some((item) => item.group === 'nav' && item.to === path && !item.isComplete);
    };

    /** Whether a settings section still needs attention — drives the Required tags in settings. */
    const isSectionRequired = (sectionKey: string) =>
        isActive.value && items.value.some((item) => item.sectionKey === sectionKey && !item.isComplete);

    const openModal = () => {
        modalVisible.value = true;
    };

    const closeModal = () => {
        modalVisible.value = false;
    };

    const seenStorageKey = () => `${SEEN_STORAGE_PREFIX}${authStore.organization?.organizationId ?? 'unknown'}`;

    const markSeen = () => {
        try {
            localStorage.setItem(seenStorageKey(), 'true');
        } catch {
            // A browser refusing storage only costs us the "show once" guarantee, which is not worth
            // failing a page load over.
        }
    };

    const hasBeenSeen = () => {
        try {
            return localStorage.getItem(seenStorageKey()) === 'true';
        } catch {
            // Assume it has been seen, so a storage-less browser is never nagged on every visit.
            return true;
        }
    };

    /**
     * Open the checklist unprompted the first time an organization that still has setup to do lands
     * on the dashboard. After that it is only ever opened from the button.
     */
    const autoOpenOnce = () => {
        if (!isActive.value || hasBeenSeen()) return;
        markSeen();
        openModal();
    };

    return {
        modalVisible,
        items,
        navItems,
        settingsItems,
        hasChecklist,
        isComplete,
        isActive,
        remainingCount,
        isSettingsRequired,
        isRouteRequired,
        isSectionRequired,
        openModal,
        closeModal,
        markSeen,
        autoOpenOnce
    };
});
